import React from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormHelperText from '@material-ui/core/FormHelperText';
import cn from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { get } from 'lodash';
import { ExpandMore, ArrowDropDown } from '@material-ui/icons';
import { colors } from '../../constants/styleConstants';
import { styles } from './FormikAutocompleteSelect';

const useStyles = makeStyles(() => ({
  formControl: {
    marginTop: '0px !important',
    zIndex: 'auto',
    '& .MuiInput-root': {
      marginTop: '0px !important',
    },
  },
  fieldLabel: {
    ...styles.selectLabel,
    position: 'relative',
    marginBottom: '2px',
  },
  fieldLabelShrinked: styles.selectLabel,
  input: {
    ...styles.selectInput,
    height: '29px !important',
    alignItems: 'center',
    '&:focus': {
      backgroundColor: styles.selectInput.backgroundColor,
    },
  },
  selectField: {
    color: colors.title,
    fontSize: 20,
    textAlign: 'left',
    padding: '7px 0px 7px 10px !important',
  },
  formHelperText: styles.formHelperText,
  icon: {
    ...styles.controlIcon,
    padding: 0,
    marginRight: 1,
    color: colors.blue,
    right: 1,
  },
  shrink: styles.shrink,
}));

function SelectField(props) {
  const {
    required,
    label,
    field: { name, value },
    form: { errors, touched, setFieldValue },
    options,
    native,
    className,
    selectOptionClassName,
    fullWidth,
    renderOption,
    onChange,
    staticLabel,
    iconType,
    ...other
  } = props;
  const classes = useStyles();

  const id = `select_${name}`;
  const hasError = get(touched, name) && Boolean(get(errors, name));
  const errorText = get(errors, name);
  const simpleSelectOptionList = options.map(item => (
    <MenuItem key={get(item, 'value')} value={get(item, 'value')}>
      {get(item, 'label')}
    </MenuItem>
  ));
  const nativeSelectOptionList = options.map(item => (
    <option key={get(item, 'value')} value={get(item, 'value')} className={selectOptionClassName}>
      {get(item, 'label')}
    </option>
  ));

  return (
    <FormControl
      fullWidth={fullWidth}
      required={required}
      error={hasError}
      classes={{
        root: cn(classes.formControl, className),
      }}
    >
      {label && (
        <InputLabel
          htmlFor={id}
          classes={{ root: classes.fieldLabel, focused: classes.fieldLabel, shrink: staticLabel && classes.shrink }}
          shrink={staticLabel}
        >
          {label}
        </InputLabel>
      )}
      <Select
        {...other}
        native={native}
        value={get(value, 'value', other.multiple ? [] : undefined)}
        disableUnderline
        onChange={event => {
          setFieldValue(name, get(event, 'target'));
          if (onChange && typeof onChange === 'function') {
            onChange(get(event, 'target'));
          }
        }}
        classes={{
          root: classes.selectField,
          select: classes.input,
          icon: cn(classes.icon, props.iconclass),
        }}
        inputProps={{
          name,
          id: `input_${id}`,
        }}
        IconComponent={iconType === 'dropdown' ? ArrowDropDown : ExpandMore}
      >
        {native && !renderOption && nativeSelectOptionList}
        {!native && !renderOption && simpleSelectOptionList}
        {renderOption && options.map((option, index) => renderOption(option, index, value))}
      </Select>
      {errorText && <FormHelperText classes={{ root: classes.formHelperText }}>{errorText}</FormHelperText>}
    </FormControl>
  );
}

export const selectOptionsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
);

SelectField.propTypes = {
  required: PropTypes.bool,
  label: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.object]),
  }),
  form: PropTypes.shape({
    dirty: PropTypes.bool,
    errors: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  options: selectOptionsPropType,
  native: PropTypes.bool,
  className: PropTypes.string,
  selectOptionClassName: PropTypes.string,
  fullWidth: PropTypes.bool,
  renderOption: PropTypes.func,
  onChange: PropTypes.func,
  staticLabel: PropTypes.bool,
};

SelectField.defaultProps = {
  native: false,
  fullWidth: true,
  staticLabel: false,
  field: {
    name: 'selectName',
    onChange: () => {},
    value: null,
  },
  form: {
    dirty: false,
    errors: {},
    setFieldValue: () => {},
  },
  options: [],
};

export default SelectField;
